import { fluidType } from 'Shared/common/FluidType';
import { sanitise } from 'Shared/common/Sanitise';
import { merge } from 'Shared/common/merge';
import { standardText } from 'Shared/frontend/themes/StandardText';
import {ILinks} from 'Common/config/ILinks';
import { VenueRoutes } from 'Shared/routes/VenueRoutes';
import { standardLinks } from 'Shared/frontend/themes/standardLinks';

import {Calendar as BackendCalendar} from 'Shared/backend/icons/Calendar';
import {ClockCircle as BackendClockCircle} from 'Shared/backend/icons/ClockCircle';
import {FoodMenu as BackendMenu} from 'Shared/backend/icons/FoodMenu';
import {InfoCircle as BackendInfoCircle} from 'Shared/backend/icons/InfoCircle';
import {StarCircle as BackendStarCircle} from 'Shared/backend/icons/StarCircle';

import { Calendar as FrontendCalendar } from 'Shared/frontend/icons/Calendar';
import { Contact as FrontendContact } from 'Shared/frontend/icons/Contact';
import { FoodMenu as FrontendMenu } from 'Shared/frontend/icons/FoodMenu';
import { Home as FrontendHome } from 'Shared/frontend/icons/Home';
import { standardStyles } from 'Shared/frontend/styles/standardStyles';
import { NavSchema } from 'Shared/frontend/nav/navSchema';
import { CustomStyle, EventSettings, FooterSettings, NavSettings, Palette, StyleTag, TextSettings, VenueSettings } from 'Shared/model/venueSettings';
import { colourRule, dimBackground, hoverBackground } from 'Shared/model/settingColour';
import { textStyleRule } from 'Shared/model/settingText';


export abstract class BaseSettings
{
	constructor(
		private venueSettings:VenueSettings
	)
	{ }

	sizes()
	{
		return {
			flipToMobileNav: 600,

			/* Sometimes its good to shrink the margins a bit when approaching flipToMobileNav... */
			startMarginShrink: 700,
			widePageSideMargin: 32,
			narrowPageSideMargin: 16,
			minDesktopWidth: 1000,
			maxPageWidth: 1200, 
			maxFooterWidth: 1300,

			/* Width of an iPhone5 is 320px, iPhone6 is 375px */
//XXX cf ==> minWidth / minPhoneWidth?			
			endFontShrink: 375,

			/* Events use a different flip point because they are so busy */
//TODO look at this use. Probably better moved to the individual page files.
			flipToOneColumn: 700,	
		};
	}

	palette(): Palette { return this.venueSettings.frontend.palette; }

	textStyles(): TextSettings { return this.venueSettings.frontend.text; }

	navSettings(): NavSettings { return this.venueSettings.frontend.nav; }

	eventSettings(): EventSettings { return this.venueSettings.frontend.event; }

	footerSettings(): FooterSettings { return this.venueSettings.frontend.footer; }

	/* The venue's own styles, built in the Style Builder - see venue.settings.frontend.customStyles,
	   edited on /admin/style-builder. standardStyles() folds them into the 'Venue styles' category. */
	customStyles(): CustomStyle[] { return this.venueSettings.frontend.customStyles ?? []; }

	/* The site-wide style list - see venue.settings.frontend.styles, edited under 'Page styles' on
	   /admin/settings. initPageStyles() is what lays these under a page; this accessor is for the one
	   caller that has no page to read a style chain off - see chosenStyleName() in buttonStyleCss.ts. */
	siteStyles(): StyleTag[] { return this.venueSettings.frontend.styles ?? []; }

	/* The venue's own location classes - see venue.settings.frontend.locationClasses, edited under
	   'Page styles' on /admin/settings. Folded into siteTheme().classes alongside the code-defined ones. */
	locationClasses(): string[] { return this.venueSettings.frontend.locationClasses ?? []; }

	abstract fontIncludes();

	abstract fonts();

	/*
		The page's body face - the venue's 'Body font' (venue.settings.frontend.text.bodyFontFamily,
		edited at the top of the Text section on /admin/settings), or the font the venue's code names
		while it has said nothing.

		This is the one font the page itself is drawn in: page().root hands it down, so everything not
		given a font of its own arrives at it - a footer address, a menu teaser, an event tagline, bare
		text in a block of embedded html. standardText() is built from it too, which is where 'Plain
		text' and 'Tagline' get theirs unless the venue names another.
	*/
	bodyFont()
	{
		const family = this.textStyles().bodyFontFamily ?? '';
		return family=='' ? this.fonts().text : family;
	}

	/* standardText() drawn in the venue's body face rather than the one its code names - see
	   bodyFont(). The other five faces fonts() carries (heading, the two navs, buttons, sub-nav) are
	   still the venue code's alone. */
	standardTextStyles()
	{
		return standardText(this.sizes(),{...this.fonts(), text: this.bodyFont()});
	}

	/*
		Every text style - colour, font, weight, size, transform, line height, letter spacing,
		alignment and margin - as a tree that merges onto standardText(). They come off the venue
		document - see venue.settings.frontend.text, edited under 'Frontend settings' on
		/admin/settings.

		Venue subclasses patch this through merge(super.text(),...), so a subclass that still spreads
		one of these properties into a field (eg a fontsHeading() helper carrying fontFamily /
		letterSpacing / lineHeight / fontWeight, which it also feeds to the nav and menu) must set that
		property to undefined on the field so the venue document's value shows - merge() falls back to
		the base value whenever a patch key is undefined. No venue subclass overrides text() any more.
	*/
	text()
	{
		const text = this.textStyles();
		const palette = this.palette();
		const base = this.standardTextStyles();

		return merge(base, <Partial<typeof base>> {
			pageTitle: {...textStyleRule(text.pageTitle,palette)},
			title:     {...textStyleRule(text.title,palette)},
			teaser:    {...textStyleRule(text.teaser,palette)},
			tagline:   {...textStyleRule(text.tagline,palette)},
			subtitle:  {...textStyleRule(text.subtitle,palette)},
			plain:     {...textStyleRule(text.plain,palette)},
		});
	}

	address()
	{
		return {
			...sanitise.address,
			lineHeight: 'normal',
			display: 'inline-block',
			verticalAlign: 'middle',
		};
	}

	/*
		The footer's divider line and venue-name heading, built entirely here now: a feature-coloured
		divider and text-coloured heading, the palette-driven values defaultFooterSettings used to
		supply. No venue ever pulled away from them; one that wants to turns on a standard style under
		'Footer' (see venue.settings.frontend.footer.styles), which ShowWrapElement resolves onto the
		footer at ['footer'] against the .footer-venue-name class.
	*/
	footer()
	{
		const palette = this.palette();

		return {
			address: this.address(),

			divider: {
				borderColor: palette.feature,
			},
			title: {
				fontFamily: this.fonts().heading,
				fontWeight: 700,
				textTransform: 'uppercase',
				margin: '8px 0',
				color: palette.text,
			},
		};
	}

	/*
		The nav: sizes, spacing and the palette-driven look every nav shows out of the box. Nothing of
		the venue's is here. What it sets is a list of rules per node - see venue.settings.frontend.nav
		and the 'Nav' section of /admin/settings - which standardStyles() carries as the 'Settings rules'
		category and ShowWrapElement lays over the node's wrapper, so a part the venue says nothing
		about keeps the value below. No venue subclass overrides nav().

		The mobile button/active 'svg.fill' carry no '!important': nothing outside these settings
		colours the icon any more.
	*/
	nav() {
		const sizes = this.sizes();
		const palette = this.palette();

		/* The background the mobile footer buttons and the 'More' menu's buttons show under the pointer.
		   Both sit on the page background - the footer buttons are painted with it below, the 'More'
		   buttons are transparent over the overlay - so both lift or drop off the same colour. */
		const hover = hoverBackground(palette.background);

		/* What all three nodes mark the page you are on with, and what the desktop bar's buttons go to
		   under the pointer. One colour so the two can't drift apart: a venue that wants its current
		   page marked some other way says so on the node's 'Current page button' part. */
		const current = palette.feature;

		return {
			desktop: <NavSchema> {
				/* FOR OPTIONS: see the NavSchema definition */

//TODO implement 'hamburger' + 'none'
				type: 'standard',

				settings: {
					logoPosition: 'left',

					sticky: true,
//NOTE fadeBackgroundIn does not currently support subnavs. Probably just want to use it for homepages.
//					fadeBackgroundIn: true,

					/* 'backgroundColor' rather than the 'background' shorthand: the 'Fade in background'
					   style in navStyles.ts reads this key back to know what to fade up to. */
					outer: {
						backgroundColor: palette.background,
					},

					inner: {
						height: 100,
						maxWidth: 950,
						/* Scavenge some space on narrow screens: */
						gap: fluidType('em',sizes.flipToMobileNav/16,sizes.minDesktopWidth/16,0,2)
					},

					logo: {
						height: '100%',
						padding: '5px 0 5px 0',
					},

					button: {
						...sanitise.button,
						fontFamily: this.fonts().desktopNav,
						fontSize: fluidType('px',sizes.endFontShrink,sizes.minDesktopWidth,16,30),
						backgroundColor: 'inherit',
						color: palette.text,
						margin: '0 0.4em',
						textTransform: 'uppercase',
						padding: '0 0',
					},
					active: {color: current},
					hover:  {color: current},
					subNav: {
						backgroundColor: palette.background,
					}
				}
			},
			mobile: {
				/* The full-screen 'More' overlay's background. It sits here, on the wrapper
				   ShowWrapElement styles, not on more.root's inner panel: a venue's 'Panel background'
				   nav rule lands flat on this wrapper, and an opaque more.root would paint over it. */
				overlay: {
					backgroundColor: palette.background,
				},

				button: {
					...sanitise.button,
					fontFamily: this.fonts().mobileNav,
					color: palette.text,
					backgroundColor: palette.background,
					padding: '0.6em 0 18px 0',

					/* An icon takes the colour of the label under it unless a rule gives it one of its
					   own. On the button rather than on the div the icon and label sit in, which
					   MobileFooterNav styles separately: two classes at the same specificity, so
					   whichever emotion happened to write last coloured the icon, and on the current
					   page that decided whether the 'active' fill below took or not. */
					svg: {fill: palette.text},

					':hover': {
						backgroundColor: hover,
					}
				},

				/* The mobile footer's current-page button, and the 'More' menu's - NavMore lays this
				   over its own buttons rather than carrying a second copy. */
				active: {
					color: current,
					svg: {fill: current},
				},

				more: {
					/* Background lives on 'overlay' above, not here - see the note there. */
					root: {},
					button: {
						textTransform: 'capitalize',  //XXX undoing theme.button
						borderStyle: 'none',
						backgroundColor: 'transparent',
						color: '#e2e2e2',
						padding: '0.8em 0',
						fontSize: fluidType('px',this.sizes().endFontShrink,this.sizes().flipToMobileNav,20,30),
						':hover': {
							backgroundColor: hover,
						}
					},
					address: this.address(),
					phone: {}
				}
			}
		};
	}

	/*
		The base every button on the site starts from - the call to action under a page heading, a
		buttons widget's, an event's Details, and the buttons of a sub-nav row. The venue's own look is
		laid over it at the button, off the 'Buttons > Button style' or 'Buttons > Sub nav button
		style' tag on the style chain - see buttonCss() and subNavButtonCss() - so that a page or a
		Styles widget can dress its buttons differently from the rest of the site.
	*/
	buttonBase()
	{
		return {
			...sanitise.button,
			fontSize: '15px',
			fontFamily: this.fonts().buttons,
			borderWidth: 1,
			padding: '0.7em 2.25em',
			textTransform: 'uppercase'
		};
	}

	/*
		The button row under a page's heading - the event lineup filters, the menu's section jumps,
		and a widget's sticky button row. All three start from this.

		A sub-nav button is an ordinary button: the same base, wearing the venue's sub-nav font and the
		palette-driven fill that a style naming no colour leaves standing. What used to make one
		smaller and differently spaced than a call to action - 15px, capitalize, '0 0.4em',
		'0.44em 2.25em' - are properties of the venue's 'Sub nav button style' now (see
		seedSubNavButtonSizing.ts), so a venue that points that tag at its ordinary button style gets
		ordinary buttons in its sub-nav rows, which is what The Drummer does.

		The venue's own look arrives at the button rather than here: the row has no wrapper for the
		resolver to land a style on, so it comes off the 'Buttons > Sub nav button style' tag on the
		style chain - see subNavButtonCss(). That is what lets a page or a Styles widget dress its own
		sub-nav row differently, which a value folded in here could not.
	*/
	subNavButton()
	{
		const palette = this.palette();

		return {
			...this.buttonBase(),
			fontFamily: this.fonts().subNav,
			backgroundColor: dimBackground(palette),
			color: palette.text,
		};
	}

	page()
	{
		const palette = this.palette();
		const fonts = this.fonts();
		const sizes = this.sizes();
		const text = this.text();

		return {
			text: text,

			/*
				Inherited through the page: the base every element that says nothing about its own
				text is drawn at, and nothing more.

				The font is the venue's, through bodyFont() - the one text property here a venue can
				move, and the only one it should have to. The size is a flat 16px and there is no line
				height at all: 16 is the browser's own font size, so an 'em' anywhere below is the
				value it reads as, and text that wants a line height says so in its own style.

				Neither followed a venue setting on purpose. They did once - root took the whole
				'Plain text' style - and that made an ambient default no caller asked for: a venue
				raising its Plain text to 16-18px quietly grew every em on a menu page, since item
				prices, the gaps between items and the small print beside an item name all hang off
				whatever their section inherited. See seedDrummerMenuStyle.ts / seedFamilyMenuStyle.ts
				for the two menus that had to be pinned back, and the menuPage components for the
				sizes that are px now because of it. 'Plain text' is applied where body text is
				actually wanted - a Plain text component, a biography, an event's description, a
				menu's details, an embedded-html <p> - and those all name it.
			*/
			root: {
				backgroundColor: palette.background,
				color: palette.text,

				fontFamily: this.bodyFont(),
				fontSize: 16,
			},

			nav: this.nav(),

			footer: this.footer(),

			sideMargins: {
				left: fluidType('px',sizes.flipToMobileNav,sizes.startMarginShrink,sizes.narrowPageSideMargin,sizes.widePageSideMargin),
				right: fluidType('px',sizes.flipToMobileNav,sizes.startMarginShrink,sizes.narrowPageSideMargin,sizes.widePageSideMargin),
			},

			main: {
				flexGrow: 1,
				marginTop: 0,
				marginBottom: 32,
				maxWidth: sizes.maxPageWidth,
			},

			section: {
				width: '100%',
				textAlign: 'center',
			},

			subNav: {
				inner: {
					marginTop: 32,
					marginBottom: 32
				}
			},

			widgets: {    //XXX cf ==> layout/design/pageDesign/showcase or promote slideshow
				overlay: {},
				slideshow: {    //TODO rename to slides for consistency
					teaser: { },
					backgroundColour: dimBackground(palette),

					overlayedImage: {
						backgroundColor: 'rgba(0, 0, 0, 0.56)',
						padding: '2em',
						minWidth: '395px',

						filter: {},
					}
				},

				subNav: {
					button: this.subNavButton()
				},
				visualWithInfo: {
					wide: {
						float: {
	    					width: '50%',
							paddingTop: 16,
							//FIXME currently this means the side alignment is off. Adding a bit of padding does reduce the size of the image a bit, but there must bit a better way
							outerPadding: 8,
							innerPadding: 16,
							innerMargin: '1%'
						},
						textAlign: 'start'
					},
					narrow: {
						textAlign: 'center'
					}
				},
			},

			a: {
				...sanitise.a,
				color: palette.feature
			},

			list: {
				...sanitise.list,
				lineHeight: 1.4,
			},

			definitions: {
				...sanitise.definitions,
				lineHeight: 1,

				dt: {
					...sanitise.definitions.dt,
					fontWeight: 'bold',
					paddingRight: '0.6em'
				},

				dl: {
					...sanitise.definitions.dl,
				}
			},

			overlay: {
				button: {
					...sanitise.button,
					fontFamily: fonts.subNav,
					fontSize: 44,
					fontWeight: 700,
					borderRadius: '50%',
					width: 44,
					height: 44,
					padding: 0,
					color: palette.text,
					backgroundColor: palette.background,
				}
			},

			blockquote:  {
				...sanitise.blockquote,
				fontStyle: 'italic',
				fontWeight: 300,
				lineHeight: 1.3,
				margin: '1em 0'
			},

			address: this.address(),

			iframe: {
				...sanitise.iframe
			},

			button: this.buttonBase(),

			embeddedHtml: {
//XXX something in staffResources might be using h1 directly				
				h1: { 
					...text.paragraphTitle,
				},
				'h2,h3,h4,h5,h6': {
					...text.paragraphTitle,
				},

				/* Weight only. A venue that wants its bold text in a different font builds a Style
				   Builder style with a rule at the 'Bold text' location class, which resolves to the
				   '.embedded-html strong' selector - see embeddedElements in ruleProperties.ts. The
				   Townie's 'Strong text' (REM) is the only one (seedEmbeddedHtmlStyles.ts). */
				strong: {
					fontWeight: 700,
				},

				a: {
					...sanitise.a,
					color: palette.feature
				},

				p: {
					...text.plain,
					margin: '16px 0',
					lineHeight: 1.5
				},

				img: {
					maxWidth: '100%',
					height: 'auto'
				}
			},
		};
	}

	pages() { return []; }


	siteTheme()
	{
		const palette = this.palette();
		const fonts = this.fonts();
		const sizes = this.sizes();
		const page = this.page();

		const eventBase = merge(this.page(), {
			subNav: {
				button: this.subNavButton()
			},
			/* The date and name headings - the venue 'Page title' text style, left aligned: an event
			   name sits above a left aligned poster, tagline and description, and 'Page title' is the
			   one text style standardText() centres. A venue whose event headings depart from this
			   turns on a style through the Event page's Styles control - 'Use Title as Page title' for
			   the venue 'Title' look, a Style Builder style for anything more, including a 'Text align'
			   rule to put the centring back. See EventPage.tsx's nameStyle(). */
			heading: {
				...page.text.pageTitle,
				textAlign: 'start',
			},
			artist: {
				heading: {
					...page.text.title,

					fontSize: fluidType('px',sizes.endFontShrink,sizes.minDesktopWidth,27,46),


				},
				/* The set time beside a wide artist's name, and the band a narrow one sits in - the
				   feature colour on every venue, as their old event settings said. The narrow name
				   itself is painted in the page background colour (see narrowNameAndTimeStyle), so
				   without the band behind it there is nothing to read. */
				wide: {
					nameAndTime: {
						color: palette.feature
					}
				},
				narrow: {
					nameAndTime: {
						backgroundColor: palette.feature
					}
				},
				/* The genres and location line under an artist's name, in the feature colour - what
				   every venue's old event settings said, bar Chippo, Darling Nikki's and Showcase 2,
				   which used the second feature colour and now carry an 'Artist genres & location'
				   style that lays it back over this (see seedEventSettings.ts). The line carries the
				   'genres' location class so such a style can reach it. */
				genresAndLocation: {
					color: palette.feature
				}
			}
		});

		/* A menu item name is the venue's 'Plain text' style bar its font size - its body face, weight
		   and any letter spacing / colour it sets. The size is left off so the item name keeps taking
		   it from the card's own responsive scale (see wideDetailsStyle in CardItem.tsx), which is
		   denser than body text on purpose. */
		const { fontSize: _plainFontSize, ...itemNameStyle } = page.text.plain;

		const menuBase = merge(page, {
			subNav: {
				button: this.subNavButton(),
			},
			section: {
				marginTop: 16,
				marginBottom: 48,
			},

			title:(level:number,productType,section) => {
				return {
					...page.text.title,
					textAlign: 'center',
					...(level > 2 ? {fontSize:'clamp(16px, 10vw, 35px)'} : {}),
				}
			},
			item: {
				/* Menu layout (image cards vs a plain list) is now chosen per menu document, not per
				   venue - a 'For specific pages > Menu image cards' style tag on the menu opts into
				   cards; without it the menu is an imageless list. See menuUsesImageCards(). */
				card: {},
				/* The venue 'Plain text' style (minus its size - see itemNameStyle above). A venue
				   whose menu item names depart from this turns on a 'Venue styles' entry that layers
				   over it - see seedMenuCustomStyles.ts. */
				title: itemNameStyle,
				description: {},
				//XXX The Townie uses different colours for different section types, so using closure here
				titleType: section => {},
				//XXX actually applied to 'priceWrap'...
				price: {},
			}
		});

		return {
			sizes: sizes, 	//TODO maybe rename to 'mediaPoints', 'turningPoints', 'pageWidths', 'widths' or something

			palette: palette,

			fonts: fonts,

			page: page,

			fontIncludes: this.fontIncludes(),

			/* 'Page heading' is the one code-defined class; the rest are the venue's own, edited under
			   'Page styles' on /admin/settings.
			   NOTE class names can't contain spaces - a space separates the class from a childKey
			   filter in style location expressions. */
			classes: [
				'Page heading',
				...this.locationClasses(),
			],

			styles: standardStyles(sizes,this.text(),this.customStyles(),this.navSettings(),
				this.footerSettings().rules ?? [],this.eventSettings().rules ?? [],palette),

			event: eventBase,

			menu: menuBase,

			staffResources: merge(page, {
				subNav: {
					inner: {
						marginTop: 16,
						marginBottom:16 ,
					}
				}
			}),

			staffResource: merge(page, {
				sideMargins: {
					left: 0,
					right:0 
				}
			}),
		};
	}

	navContent()
	{
		return {
			desktop: [
				{label:'Home',		link:'frontend/Home'},
				{label:'Menu',		link:'frontend/Menu'},
				{label:'Events',	link:'frontend/Events'},
				{label:'Hire',		link:'frontend/Hire'},
				{label:'Contact',	link:'frontend/Contact'}
			],
			mobile: {
				maxButtons: 5,
				includeMoreButton: true,
				links: [
					{label:'Home',		link:'frontend/Home',		icon: FrontendHome},
					{label:'Menu',		link:'frontend/Menu',		icon: FrontendMenu},
					{label:'Events',	link:'frontend/Events',		icon: FrontendCalendar},
					{label:'Hire',		link:'frontend/Hire', 		icon: FrontendContact},
					{label:'Contact',	link:'frontend/Contact',	icon: FrontendContact},
				]
			}
		};
	}

	backendNavContent()
	{
		return [
			{label:'Dashboard',	route:'/admin/dashboard',		icon:BackendClockCircle},
			{label:'Events',	route:'/admin/events',			icon:BackendCalendar},
			{label:'Menu',		route:'/admin/menus',			icon:BackendMenu},
			{label:'Specials',	route:'/admin/specials',		icon:BackendStarCircle},
			{label:'Info',		route:'/admin/info',			icon:BackendInfoCircle},
		];
	}

	/*
		The backend events-screen settings - just the default start time createEvent() gives a new
		one-off event. Comes off the venue document; see venue.settings.frontend.event, edited under
		'Event page' on /admin/settings. defaultStart is an 'HH:MM:SS' string.
	*/
	backendEvent()
	{
		return {
			event: this.eventSettings(),
		};
	}

	links(): ILinks
	{
		return standardLinks();
	}

	routes()
	{
		return VenueRoutes;
	}
}

export type BaseThemeType = ReturnType<BaseSettings['siteTheme']>;

export type BasePageThemeType = ReturnType<BaseSettings['page']>;

export type BaseSizesType = ReturnType<BaseSettings['sizes']>;

